import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "react-bootstrap";

import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { Box, FormControl, FormHelperText, OutlinedInput } from "@mui/material";
import { toast } from "react-toastify";
import * as userActions from "../../../actions/index";
import { connect } from "react-redux";
import { display } from "@mui/system";
import moment from "moment";
import AspectRatioIcon from '@mui/icons-material/AspectRatio';

const LOCATION_HEADERS = ["parent1_country", "parent2_country", "parent1_state", "parent2_state", "parent1_city", "parent2_city"];

const ImportCsvModal = ({ showModal, csv_student_details, setCsv_student_details, import_student_form, showModalHandler, getDatabyFirstModal, openSecondModal, onGetImportStudentDetails, onGetReviewAgainImportStudents, onSearchLocation, dormitory_id }) => {
    const [csv_student_detail, setCsv_student_detail] = useState([])
    const [emailDomian, setEmailDomian] = useState("")
    const [dimensions, setDimensions] = useState({ width: 1650, height: 600 });
    const [position, setPosition] = useState({ top: 50, left: 50 });
    const [isFinalValid, setIsFinalValid] = useState(false)
    const [activeSearch, setActiveSearch] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [locationSearchEmpty, setLocationSearchEmpty] = useState(false);
    const [locationIds, setLocationIds] = useState({});
    const searchTimerRef = useRef(null);
    const locationIdsRef = useRef({});
    const pendingResolutionsRef = useRef({});

    useEffect(() => {
        setCsv_student_detail(csv_student_details)
        setLocationIds({});
        locationIdsRef.current = {};
        const user_info = JSON.parse(localStorage.getItem("bss_authuser"));
        if (user_info?.email) {
            const startPoint = user_info.email.lastIndexOf("@")
            const email_extansion = user_info.email.slice(startPoint, user_info.email.length);
            setEmailDomian(email_extansion)
        }
    }, [csv_student_details])

    useEffect(() => {
        locationIdsRef.current = locationIds;
    }, [locationIds])

    let headers1 = csv_student_detail[0] && Object.keys(csv_student_detail?.[0]);
    let headers = headers1 && headers1.filter(item => item !== "valid" && item !== "error");

    // HELPER: Return ALL error messages from the row joined into one string (lowercase)
    // so every validateCell includes(...) check fires at the same time
    const getErrorStr = (row) => {
        if (!row?.error) return "";
        const list = Array.isArray(row.error[0]) ? row.error[0] : row.error;
        return list.map(e => e?.toString().toLowerCase()).filter(Boolean).join(" | ");
    };

    // Pick one specific raw error message from the list that matches a keyword
    const getRawErrorForKeyword = (row, keyword) => {
        if (!row?.error) return "";
        const list = Array.isArray(row.error[0]) ? row.error[0] : row.error;
        const match = list.find(e => e?.toString().toLowerCase().includes(keyword));
        return match?.toString() ?? "";
    };

    // Validation function for cells
    const validateCell = (header, value, row) => {
        const errorStr = getErrorStr(row);

        const backendValidationEmail = errorStr?.includes("email") || row?.student_email === "";
        const backendValidationDateOfBirth = errorStr?.includes("age") || errorStr?.includes("4-25") || errorStr?.includes("years of age") || row["date_of_birth(DD-MM-YYYY)"] === "";
        const backendValidationKiosk = errorStr?.includes("kiosk") || row?.kiosk_mode_pin === "";
        const backendValidationUnique = errorStr?.includes("unique") || row?.unique_pin === "";
        const backendValidationGender = errorStr?.includes("male-only dormitory") || errorStr?.includes("female-only dormitory");

        const ALLOWED_PARENT_TYPES = ["father", "mother", "guardian", "other"];
        const backendValidationParent1Type = errorStr?.includes("parent 1 type");
        const backendValidationParent2Type = errorStr?.includes("parent 2 type");

        // Country, State, City API validation checks
        const p1CountryErr = errorStr?.includes("parent 1 invalid country");
        const p2CountryErr = errorStr?.includes("parent 2 invalid country");
        const p1StateErr = errorStr?.includes("parent 1 invalid state");
        const p2StateErr = errorStr?.includes("parent 2 invalid state");
        const p1CityErr = errorStr?.includes("parent 1 invalid city");
        const p2CityErr = errorStr?.includes("parent 2 invalid city");

        switch (header) {
            case "kiosk_mode_pin":
                return value === "" || value?.trim().length === 6 && !backendValidationKiosk;
            case "preferred_name":
                return /^[\p{L}' -]+$/u.test(value) && value?.trim() !== "";
            case "student_first_name":
                return /^[\p{L}' -]+$/u.test(value) && value?.trim() !== "";
            case "parent1_name":
            case "parent2_name":
                return /^[\p{L}' -]+$/u.test(value) && value?.trim() !== "";
            case "student_email":
                return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) !== "" && !backendValidationEmail;
            case "gender":
                return (value.toLowerCase() === "male" || value.toLowerCase() === "female") && !backendValidationGender;
            case "parent1_as_host":
            case "parent2_as_host":
                return value.toLowerCase() === "yes" || value.toLowerCase() === "no";
            case "parent1_type":
                return ALLOWED_PARENT_TYPES.includes(value?.trim().toLowerCase()) && !backendValidationParent1Type;
            case "parent2_type":
                return ALLOWED_PARENT_TYPES.includes(value?.trim().toLowerCase()) && !backendValidationParent2Type;
            case "student_mobile_cell":
            case "father_mobile_cell (RemoveZero)":
            case "mother_mobile_cell (RemoveZero)":
            case "parent1_home_phone":
            case "parent1_work_phone":
            case "parent2_home_phone":
            case "parent2_work_phone":
                return value === "" || /^[0-9]{1,15}$/.test(value);
            case "parent1_email":
            case "parent2_email":
            case "tutor_email":
                return value === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            case "date_of_birth(DD-MM-YYYY)":
            case "date_of_birth(DD/MM/YYYY)":
                // First check format
                if (value?.trim() === "" || !/^[0-9\-\/]+$/.test(value)) return false;
                // Then check backend age validation
                if (backendValidationDateOfBirth) return false;
                return true;
            case "unique_pin":
                return value === "" || value?.trim().length === 4 && !backendValidationUnique;

            // Country, State, City validations
            case "parent1_country": return value?.trim() !== "" && !p1CountryErr;
            case "parent2_country": return !p2CountryErr;
            case "parent1_state": return !p1StateErr;
            case "parent2_state": return !p2StateErr;
            case "parent1_city": return !p1CityErr;
            case "parent2_city": return !p2CityErr;

            case "parent1_address_line1":
            case "parent1_address_line2":
            case "parent2_address_line1":
            case "parent2_address_line2":
            case "student_last_name":
            case "campus_name":
            case "sporting_house":
            case "laundry_number":
            case "tutor_caregroup_teachername":
            case "parent1_postcode":
            case "parent2_postcode":
                return true;
            default:
                return value?.trim() !== "";
        }
    };

    const handleCellChange = (rowIndex, header, value, row) => {
        const updatedData = [...csv_student_detail];
        updatedData[rowIndex][header] = value;
        updatedData[rowIndex].valid = validateCell(header, value, row);
        setCsv_student_detail(updatedData);
    };

    const clearStateCityFields = (rowIndex, parentPrefix) => {
        // Clear stored state_id from the ref and state immediately
        const clearedRowIds = {
            ...(locationIdsRef.current[rowIndex] || {}),
            [`${parentPrefix}_state_id`]: null,
        };
        locationIdsRef.current = { ...locationIdsRef.current, [rowIndex]: clearedRowIds };
        setLocationIds(prev => ({
            ...prev,
            [rowIndex]: { ...(prev[rowIndex] || {}), [`${parentPrefix}_state_id`]: null },
        }));
        // Clear the actual text values for state and city in that row
        setCsv_student_detail(prev => {
            const updatedData = [...prev];
            updatedData[rowIndex] = {
                ...updatedData[rowIndex],
                [`${parentPrefix}_state`]: "",
                [`${parentPrefix}_city`]: "",
            };
            return updatedData;
        });
    };

    const storeResolvedId = (rowIndex, key, id) => {
        locationIdsRef.current = {
            ...locationIdsRef.current,
            [rowIndex]: { ...(locationIdsRef.current[rowIndex] || {}), [key]: id },
        };
        setLocationIds(prev => ({
            ...prev,
            [rowIndex]: { ...(prev[rowIndex] || {}), [key]: id },
        }));
    };

    // Extract numeric id from an API result item regardless of field name convention
    const extractId = (item) =>
        item?.id ?? item?.country_id ?? item?.state_id ?? item?.city_id ?? null;

    // Extract display name from an API result item regardless of field name convention
    const getItemDisplayName = (item) =>
        item?.name || item?.country_name || item?.state_name || item?.city_name || "";

    // Find the best match from a results array by name and return its id
    const resolveIdFromResults = (results, nameText) => {
        if (!Array.isArray(results) || results.length === 0) return null;
        const lower = nameText.toLowerCase();
        const match = results.find(item =>
            getItemDisplayName(item).toLowerCase() === lower
        ) || results[0];
        return extractId(match);
    };

    // Called on focus: resolves the dependent ID (country_id for state, state_id for city)
    // in the background so it is ready before the user finishes typing.
    // pendingResolutionsRef prevents the same lookup from running twice concurrently.
    const handleLocationFocus = (rowIndex, header, row) => {
        const parentPrefix = header.startsWith("parent1") ? "parent1" : "parent2";
        const rowLocationIds = locationIdsRef.current[rowIndex] || {};

        if (header.endsWith("_state")) {
            if (rowLocationIds[`${parentPrefix}_country_id`]) return;
            const countryText = row[`${parentPrefix}_country`]?.trim();
            if (!countryText) return;
            const key = `${rowIndex}_${parentPrefix}_country`;
            if (pendingResolutionsRef.current[key]) return;
            pendingResolutionsRef.current[key] = true;
            onSearchLocation("country", countryText, {}).then(res => {
                const resolved = resolveIdFromResults(res?.data, countryText);
                if (resolved) storeResolvedId(rowIndex, `${parentPrefix}_country_id`, resolved);
            }).finally(() => { delete pendingResolutionsRef.current[key]; });

        } else if (header.endsWith("_city")) {
            const stateText = row[`${parentPrefix}_state`]?.trim();
            if (!stateText) return;

            const resolveStateId = (countryId) => {
                if (locationIdsRef.current[rowIndex]?.[`${parentPrefix}_state_id`]) return;
                const stateKey = `${rowIndex}_${parentPrefix}_state`;
                if (pendingResolutionsRef.current[stateKey]) return;
                pendingResolutionsRef.current[stateKey] = true;
                const stateParams = countryId ? { country_id: countryId } : {};
                onSearchLocation("state", stateText, stateParams).then(res => {
                    const resolved = resolveIdFromResults(res?.data, stateText);
                    if (resolved) storeResolvedId(rowIndex, `${parentPrefix}_state_id`, resolved);
                }).finally(() => { delete pendingResolutionsRef.current[stateKey]; });
            };

            const existingCountryId = rowLocationIds[`${parentPrefix}_country_id`];
            if (existingCountryId) {
                resolveStateId(existingCountryId);
            } else {
                const countryText = row[`${parentPrefix}_country`]?.trim();
                if (!countryText) { resolveStateId(null); return; }
                const countryKey = `${rowIndex}_${parentPrefix}_country`;
                if (pendingResolutionsRef.current[countryKey]) return;
                pendingResolutionsRef.current[countryKey] = true;
                onSearchLocation("country", countryText, {}).then(res => {
                    const resolvedCountryId = resolveIdFromResults(res?.data, countryText);
                    if (resolvedCountryId) storeResolvedId(rowIndex, `${parentPrefix}_country_id`, resolvedCountryId);
                    resolveStateId(resolvedCountryId);
                }).finally(() => { delete pendingResolutionsRef.current[countryKey]; });
            }
        }
    };

    const handleLocationSearch = (rowIndex, header, value, row) => {
        handleCellChange(rowIndex, header, value, row);

        if (header.endsWith("_country")) {
            const parentPrefix = header.startsWith("parent1") ? "parent1" : "parent2";
            clearStateCityFields(rowIndex, parentPrefix);
        }

        setActiveSearch({ rowIndex, header });

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (!value.trim()) {
            setSearchResults([]);
            setLocationSearchEmpty(false);
            return;
        }
        setLocationSearchEmpty(false);

        searchTimerRef.current = setTimeout(async () => {
            const parentPrefix = header.startsWith("parent1") ? "parent1" : "parent2";
            const rowLocationIds = locationIdsRef.current[rowIndex] || {};
            let type, extraParams = {};

            if (header.endsWith("_country")) {
                type = "country";
            } else if (header.endsWith("_state")) {
                type = "state";
                const country_id = rowLocationIds[`${parentPrefix}_country_id`];
                if (country_id) extraParams.country_id = country_id;
            } else if (header.endsWith("_city")) {
                type = "city";
                const state_id = rowLocationIds[`${parentPrefix}_state_id`];
                const country_id = rowLocationIds[`${parentPrefix}_country_id`];
                if (state_id) extraParams.state_id = state_id;
                if (country_id) extraParams.country_id = country_id;
            }

            if (type) {
                const response = await onSearchLocation(type, value.trim(), extraParams);
                if (Array.isArray(response?.data) && response.data.length > 0) {
                    setSearchResults(response.data);
                    setLocationSearchEmpty(false);
                } else {
                    setSearchResults([]);
                    setLocationSearchEmpty(true);
                }
            }
        }, 500);
    };

    const handleLocationSelect = (rowIndex, header, item) => {
        const currentRow = csv_student_detail[rowIndex];
        handleCellChange(rowIndex, header, getItemDisplayName(item), currentRow);

        const parentPrefix = header.startsWith("parent1") ? "parent1" : "parent2";
        const itemId = extractId(item);

        // Update the ref immediately so the next setTimeout can read the fresh id
        // without waiting for the React state update cycle
        const updatedRowIds = { ...(locationIdsRef.current[rowIndex] || {}) };
        if (header.endsWith("_country")) {
            updatedRowIds[`${parentPrefix}_country_id`] = itemId;
            updatedRowIds[`${parentPrefix}_state_id`] = null;
            clearStateCityFields(rowIndex, parentPrefix);
        } else if (header.endsWith("_state")) {
            updatedRowIds[`${parentPrefix}_state_id`] = itemId;
        }
        locationIdsRef.current = { ...locationIdsRef.current, [rowIndex]: updatedRowIds };

        setLocationIds(prev => {
            const rowIds = { ...(prev[rowIndex] || {}) };
            if (header.endsWith("_country")) {
                rowIds[`${parentPrefix}_country_id`] = itemId;
                rowIds[`${parentPrefix}_state_id`] = null;
            } else if (header.endsWith("_state")) {
                rowIds[`${parentPrefix}_state_id`] = itemId;
            }
            return { ...prev, [rowIndex]: rowIds };
        });

        setSearchResults([]);
        setLocationSearchEmpty(false);
        setActiveSearch(null);
    };

    const validateTableData = (data) => {
        for (let row of data) {
            for (let key in row) {
                const value = row[key];
                if (!validateCell(key, value, row)) {
                    return false;
                }
            }
        }
        return true;
    };

    const handleUploadClick = () => {
        const filteredCsvStudentDetails = csv_student_detail.map(student => {
            const { error, valid, ...filteredStudent } = student;
            return filteredStudent;
        });

        onGetReviewAgainImportStudents(JSON.stringify({ dormitory_id, students: filteredCsvStudentDetails }))
            .then((response) => {
                if (response.success === true) {
                    setCsv_student_detail(response.data)
                    const allFieldsValid = response?.data?.every(item => item.valid === true);

                    if (validateTableData(filteredCsvStudentDetails) && allFieldsValid) {
                        setIsFinalValid(true);
                    } else {
                        toast.error("Please correct all errors or fill in all required fields before proceeding.", {
                            position: toast.POSITION.TOP_CENTER,
                        });
                    }
                } else {
                    toast.error(response?.response?.data?.error || "Please correct all errors.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            })
            .catch((error) => {
                console.error("Error during upload:", error);
                alert("An error occurred during upload.");
            });
    };

    const handleMouseDown = (e) => {
        const startWidth = dimensions.width;
        const startHeight = dimensions.height === "auto" ? 400 : dimensions.height;
        const startX = e.clientX;
        const startY = e.clientY;

        const handleMouseMove = (e) => {
            const newWidth = startWidth + (e.clientX - startX);
            const newHeight = startHeight + (e.clientY - startY);
            setDimensions({
                width: Math.max(newWidth, 200),
                height: Math.max(newHeight, 200),
            });
        };

        const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    useEffect(() => {
        const close = (e) => {
            if (e.keyCode === 27) showModalHandler(false);
        }
        window.addEventListener('keydown', close)
        return () => window.removeEventListener('keydown', close)
    }, [])

    // HELPER FUNCTION: Cleanly decide the error text
    const getErrorMessage = (header, row) => {
        const errorStr = getErrorStr(row);

        if (header === "student_email")
            return errorStr.includes("email") ? "Email already exists or invalid domain." : "Invalid email address";

        if (header === "gender") {
            if (errorStr.includes("male-only dormitory") || errorStr.includes("female-only dormitory"))
                return getRawErrorForKeyword(row, "dormitory").replace(/\s+at row\s+\d+/i, "");
            return "Invalid gender";
        }

        if (header === "student_mobile_cell")
            return "Invalid phone number";

        if (header === "parent1_type")
            return "Parent 1 type must be one of: father, mother, guardian, other";
        if (header === "parent2_type")
            return "Parent 2 type must be one of: father, mother, guardian, other";

        // DATE OF BIRTH: Show age-specific error message clearly
        if (header === "date_of_birth(DD-MM-YYYY)" || header === "date_of_birth(DD/MM/YYYY)") {
            if (
                errorStr.includes("age") ||
                errorStr.includes("4-25") ||
                errorStr.includes("years of age")
            ) {
                return "Student must be between 4–25 years of age.";
            }
            return "Invalid date of birth (DD-MM-YYYY)";
        }

        if (header === "kiosk_mode_pin")
            return errorStr.includes("kiosk")
                ? "Kiosk mode pin already exists or must be unique or must be 6 digit."
                : "This field is required";

        if (header === "unique_pin")
            return errorStr.includes("unique")
                ? "Unique Student pin already exists or must be unique or must be 4 digit."
                : "This field is required";

        // Country, State, City error messages
        if (header === "parent1_country")
            return errorStr.includes("parent 1 invalid country") ? "Invalid Country" : "This field is required";
        if (header === "parent2_country")
            return errorStr.includes("parent 2 invalid country") ? "Invalid Country" : "This field is required";
        if (header === "parent1_state")
            return errorStr.includes("parent 1 invalid state") ? "Invalid State" : "This field is required";
        if (header === "parent2_state")
            return errorStr.includes("parent 2 invalid state") ? "Invalid State" : "This field is required";
        if (header === "parent1_city")
            return errorStr.includes("parent 1 invalid city") ? "Invalid City" : "This field is required";
        if (header === "parent2_city")
            return errorStr.includes("parent 2 invalid city") ? "Invalid City" : "This field is required";

        return "This field is required";
    };


    return (
        <>
            <Modal
                backdrop="static"
                animation={false}
                className="confirm_delete_modal confirm_modal"
                show={showModal}
                aria-labelledby="lg-modal"
                size="xl"
            >
                <Box
                    sx={{
                        position: "fixed",
                        top: `${position.top}%`,
                        left: `${position.left}%`,
                        transform: "translate(-50%, -50%)",
                        width: `${dimensions.width}px`,
                        height: `${dimensions.height}px`,
                        maxWidth: "95vw",
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        opacity: 0,
                        animation: "importModalFadeIn 0.22s ease-out forwards",
                        "@keyframes importModalFadeIn": {
                            from: { opacity: 0, transform: "translate(-50%, -50%) scale(0.97)" },
                            to: { opacity: 1, transform: "translate(-50%, -50%) scale(1)" },
                        },
                    }}
                >
                    <Modal.Header style={{ flexShrink: 0, padding: "16px 24px" }}>
                        <Modal.Title>Import Students</Modal.Title>
                        <button type="reset" onClick={() => showModalHandler(false, csv_student_detail)} >
                            <i className="fa fa-times" aria-hidden="true"></i>
                        </button>
                    </Modal.Header>
                    <Modal.Body style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: "0px", display: "flex", flexDirection: "column" }}>
                        <TableContainer
                            className="import-students-scroll"
                            style={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "auto" }}
                        >
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {headers?.map((header, index) => {
                                            return <TableCell key={index}>
                                                {header === "date_of_birth(DD-MM-YYYY)" || header === "date_of_birth(DD/MM/YYYY)"
                                                    ? "Date Of Birth"
                                                    : header
                                                        .replace(/_/g, ' ')
                                                        .replace(/\b\w/g, (char) => char.toUpperCase())
                                                }
                                            </TableCell>
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {csv_student_detail?.map((row, rowIndex) => (
                                        <TableRow hover key={rowIndex}>
                                            {headers?.map((header, colIndex) => {
                                                const value = row[header] || "";
                                                const isValid = validateCell(header, value, row);
                                                const isLocationField = LOCATION_HEADERS.includes(header);
                                                const isActiveSearch = activeSearch?.rowIndex === rowIndex && activeSearch?.header === header;

                                                return (
                                                    <TableCell key={colIndex}>
                                                        <div style={{ position: "relative" }}>
                                                            <FormControl
                                                                sx={{ width: header === "student_email" ? "40ch" : "25ch" }}
                                                                error={!isValid}
                                                            >
                                                                <OutlinedInput
                                                                    type="text"
                                                                    value={value}
                                                                    onChange={(e) => {
                                                                        if (isLocationField && !isFinalValid) {
                                                                            handleLocationSearch(rowIndex, header, e.target.value, row);
                                                                        } else {
                                                                            handleCellChange(rowIndex, header, e.target.value, row);
                                                                        }
                                                                    }}
                                                                    onFocus={() => {
                                                                        if (isLocationField && !isFinalValid) {
                                                                            setActiveSearch({ rowIndex, header });
                                                                            handleLocationFocus(rowIndex, header, row);
                                                                        }
                                                                    }}
                                                                    onBlur={() => {
                                                                        if (isLocationField) {
                                                                            // Capture at blur time — before any async re-render
                                                                            const shouldClear = locationSearchEmpty;
                                                                            setTimeout(() => {
                                                                                if (shouldClear) {
                                                                                    // Wipe the invalid text the user typed
                                                                                    setCsv_student_detail(prev => {
                                                                                        const updated = [...prev];
                                                                                        updated[rowIndex] = { ...updated[rowIndex], [header]: "" };
                                                                                        return updated;
                                                                                    });
                                                                                    // If the invalid value was in a country field,
                                                                                    // also clear its dependent state / city
                                                                                    if (header.endsWith("_country")) {
                                                                                        const pfx = header.startsWith("parent1") ? "parent1" : "parent2";
                                                                                        clearStateCityFields(rowIndex, pfx);
                                                                                    }
                                                                                }
                                                                                setActiveSearch(prev =>
                                                                                    prev?.rowIndex === rowIndex && prev?.header === header ? null : prev
                                                                                );
                                                                                setSearchResults([]);
                                                                                setLocationSearchEmpty(false);
                                                                            }, 200);
                                                                        }
                                                                    }}
                                                                    readOnly={isFinalValid}
                                                                />
                                                                {!isValid && (
                                                                    <FormHelperText sx={{ marginLeft: "0px" }}>
                                                                        {getErrorMessage(header, row)}
                                                                    </FormHelperText>
                                                                )}
                                                            </FormControl>
                                                            {isLocationField && isActiveSearch && (searchResults.length > 0 || locationSearchEmpty) && (
                                                                <Paper
                                                                    style={{
                                                                        position: "absolute",
                                                                        top: "100%",
                                                                        left: 0,
                                                                        zIndex: 9999,
                                                                        minWidth: "100%",
                                                                        maxHeight: "200px",
                                                                        overflowY: "auto",
                                                                        marginTop: "2px",
                                                                    }}
                                                                >
                                                                    {searchResults.length > 0 ? (
                                                                        searchResults.map((item, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                style={{ padding: "8px 12px", cursor: "pointer" }}
                                                                                onMouseDown={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleLocationSelect(rowIndex, header, item);
                                                                                }}
                                                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f5f5f5"; }}
                                                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                                                                            >
                                                                                {getItemDisplayName(item)}
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div style={{ padding: "8px 12px", color: "#999", fontSize: "13px" }}>
                                                                            No data found. Please enter a valid value.
                                                                        </div>
                                                                    )}
                                                                </Paper>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Modal.Body>
                    <Modal.Footer style={{ backgroundColor: "white", flexShrink: 0, padding: "10px", position: "relative" }}>
                        <div className="d-flex align-items-end">
                            <button type="reset" className="btn btn-sm btn-light me-2 min-150px ms-auto" onClick={() => showModalHandler(false, csv_student_detail)} >
                                Cancel
                            </button>
                            {
                                isFinalValid ?
                                    <button
                                        type="button"
                                        onClick={() => openSecondModal(csv_student_detail)}
                                        className="btn btn-sm btn-primary apply-btn min-150px ms-auto">
                                        Upload
                                    </button>
                                    :
                                    <button
                                        type="button"
                                        onClick={handleUploadClick}
                                        className="btn btn-sm btn-primary apply-btn min-150px ms-auto">
                                        Validate
                                    </button>
                            }
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                bottom: "4px",
                                right: "4px",
                                width: "20px",
                                height: "20px",
                                backgroundColor: "transparent",
                                cursor: "nwse-resize",
                            }}
                            onMouseDown={handleMouseDown}
                        ><AspectRatioIcon /></div>
                    </Modal.Footer>
                </Box>
            </Modal >
        </>
    )
};

export default ImportCsvModal;